// Group sign-off dialog for a consolidated shop-floor row.
//
// The operator enters ONE total quantity (e.g. "10" across two WOs of 5);
// the dialog splits it earliest-due-first across the group's source work
// orders — and each row's share is EDITABLE, so the operator can decide
// exactly how many units go to which work order before signing off.
// Typing in the total re-runs the auto-split (overwriting manual edits);
// editing a row updates that row and the total becomes the sum.
// Every source row shows exactly which customer order it belongs to, so
// the operator always knows where the units are going.
//
// Scrap is entered ONCE for the whole group, not per order. Scrap is EXTRA
// to the good count ("made 6, scrapped 2" = 8 attempts): it never counts
// towards a work order's remaining need. The whole scrap total is recorded
// against the most urgent work order that receives good units (or the most
// urgent eligible one when nothing good was made), so the replacement is
// planned on the order that needs it soonest. The server plans and checks
// the replacement material, so only the good units are pre-flighted here.
//
// Material pre-flight runs for EVERY allocation before anything is written;
// one shortage aborts the whole batch (no half-signed groups).
//
// Each source's "can make" is fetched on open and the auto-split fills WOs
// only up to that first. Un-issued production is earmarked per order tree,
// so a group can have e.g. 4 makeable on one order's WO and 0 on another's —
// an earliest-due split that ignored this sent units to the WO that couldn't
// make them and the pre-flight then blocked the whole sign-off.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Stack, TextField, Typography,
} from '@mui/material';
import { Factory, PlayArrow, SubdirectoryArrowRight } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { v2 } from '../../../../services/api';
import {
  allocateGroupQuantity, compareByDueThenNumber,
} from '../utils/allocateGroupQuantity';
import { orderStatusColor, orderStatusLabel } from '../utils/orderStatus';
import MaterialShortageDialogV2 from './MaterialShortageDialogV2';

export default function GroupProductionDialogV2({
  open, onClose, sources = [], onDone, groupLabel = '',
}) {
  const [qty, setQty] = useState('');
  // Per-WO editable share: step_id -> string. Seeded by the auto-split,
  // then the operator can redirect units between work orders.
  const [rowQty, setRowQty] = useState({});
  // Scrapped units for the whole group. Extra to the good count.
  const [scrap, setScrap] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shortage, setShortage] = useState(null); // {woNumber, shortages}
  // step_id -> units that WO can make now (null = unknown, uncapped).
  const [capacity, setCapacity] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(false);

  const isEligible = (s) => s && (s.status === 'planned' || s.status === 'in_progress');
  const remainingOf = (s) => {
    const total = parseFloat(s.quantity_to_produce) || 0;
    const done = parseFloat(s.quantity_completed) || 0;
    return Math.max(0, total - done);
  };

  const numericQty = parseFloat(qty) || 0;
  const totalScrap = parseFloat(scrap) || 0;
  const scrapNegative = totalScrap < 0;

  // List rows in the exact order the auto-split fills them (earliest due
  // first), so entering a total visibly fills row 1, then row 2, then
  // row 3 — never a scatter.
  const orderedSources = useMemo(
    () => [...sources].sort(compareByDueThenNumber),
    [sources],
  );

  const groupRemaining = useMemo(
    () => sources.reduce((t, s) => (isEligible(s) ? t + remainingOf(s) : t), 0),
    [sources],
  );

  const applyAutoSplit = (total, cap = capacity) => {
    const { allocations } = allocateGroupQuantity(sources, total, cap);
    const next = {};
    sources.forEach((s) => {
      const a = allocations.find((x) => x.step.step_id === s.step_id);
      next[s.step_id] = String(a ? a.quantity : 0);
    });
    setRowQty(next);
  };

  // Prefill with the group's full remaining need — most sign-offs are "we
  // made the lot", so the operator usually just hits the button.
  // Once capacity is known, prefill with what can actually be made instead.
  useEffect(() => {
    if (!open) return undefined;
    setQty(groupRemaining > 0 ? String(groupRemaining) : '');
    setCapacity(null);
    applyAutoSplit(groupRemaining, null);

    let cancelled = false;
    const eligible = sources.filter((s) => isEligible(s) && remainingOf(s) > 0);
    setCapacityLoading(eligible.length > 0);
    Promise.all(eligible.map(async (s) => {
      try {
        const res = await v2.workOrders.materialAvailability(
          s.work_order_id, { quantity: remainingOf(s), step: s.step_id },
        );
        // parseFloat, not parseInt — see MaterialAwareStartButtonV2.
        let max = Math.floor(parseFloat(res.data?.max_producible));
        if (Number.isNaN(max)) return [s.step_id, null];
        // A later step can't outrun what its predecessor has released.
        if (s.depends_on_id != null && s.qty_available_from_previous_step != null) {
          const prev = parseFloat(s.qty_available_from_previous_step);
          if (!Number.isNaN(prev)) max = Math.min(max, Math.floor(prev));
        }
        return [s.step_id, Math.max(0, max)];
      } catch (err) {
        return [s.step_id, null];
      }
    })).then((pairs) => {
      if (cancelled) return;
      const cap = Object.fromEntries(pairs);
      setCapacity(cap);
      const makeable = eligible.reduce((t, s) => {
        const c = cap[s.step_id];
        return t + (c == null ? remainingOf(s) : Math.min(remainingOf(s), c));
      }, 0);
      const prefill = makeable > 0 ? makeable : groupRemaining;
      setQty(prefill > 0 ? String(prefill) : '');
      applyAutoSplit(prefill, cap);
    }).finally(() => {
      if (!cancelled) setCapacityLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupRemaining]);

  const handleTotalChange = (v) => {
    setQty(v);
    applyAutoSplit(parseFloat(v) || 0);
  };

  const handleRowChange = (s, v) => {
    const next = { ...rowQty, [s.step_id]: v };
    setRowQty(next);
    const sum = sources.reduce(
      (t, src) => (isEligible(src) ? t + (parseFloat(next[src.step_id]) || 0) : t),
      0,
    );
    setQty(String(sum));
  };

  // The rows ARE the allocation — the total is derived from them. The
  // group's scrap rides on the first (most urgent) row with good units, or
  // the most urgent eligible row when nothing good was made.
  const allocations = useMemo(() => {
    const rows = orderedSources
      .filter(isEligible)
      .map((s) => ({ step: s, quantity: parseFloat(rowQty[s.step_id]) || 0, scrap: 0 }));
    if (totalScrap > 0 && rows.length > 0) {
      const carrier = rows.find((r) => r.quantity > 0) || rows[0];
      carrier.scrap = totalScrap;
    }
    return rows.filter((a) => a.quantity > 0 || a.scrap > 0);
  }, [orderedSources, rowQty, totalScrap]);
  const totalAllocated = allocations.reduce((t, a) => t + a.quantity, 0);
  const anyRowOver = sources.some(
    (s) => isEligible(s) && (parseFloat(rowQty[s.step_id]) || 0) > remainingOf(s),
  );
  const anyRowNegative = sources.some(
    (s) => isEligible(s) && (parseFloat(rowQty[s.step_id]) || 0) < 0,
  );
  // Total typed in exceeds what the group can absorb.
  const leftover = Math.max(0, numericQty - totalAllocated);

  const canSubmit = !submitting && !capacityLoading
    && (totalAllocated > 0 || totalScrap > 0)
    && !anyRowOver && !anyRowNegative && !scrapNegative && leftover <= 0;

  const handleClose = () => {
    if (submitting) return;
    setQty('');
    setRowQty({});
    setScrap('');
    setShortage(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Phase 1 — pre-flight every allocation before writing anything.
      // Good units only: the server plans and checks the scrap's material.
      for (const a of allocations) {
        if (a.quantity <= 0) continue;
        const res = await v2.workOrders.materialAvailability(
          a.step.work_order_id,
          { quantity: a.quantity, step: a.step.step_id },
        );
        if (res.data?.shortages?.length > 0) {
          setShortage({
            woNumber: a.step.wo_number,
            shortages: res.data.shortages,
          });
          return;
        }
      }

      // Phase 2 — sign each one off. Complete starts a planned step itself,
      // issuing materials only for this WO's share (partial sign-off).
      for (const a of allocations) {
        await v2.workOrders.completeStep(
          a.step.work_order_id,
          a.step.step_id,
          a.scrap > 0
            ? { quantity_good: a.quantity, quantity_scrapped: a.scrap }
            : { quantity_good: a.quantity },
        );
      }

      toast.success(
        `Signed off ${totalAllocated} unit${totalAllocated === 1 ? '' : 's'} across `
        + `${allocations.length} work order${allocations.length === 1 ? '' : 's'}`
        + (totalScrap > 0 ? ` (${totalScrap} scrapped)` : ''),
      );
      setQty('');
      setRowQty({});
      setScrap('');
      if (onDone) onDone();
      onClose();
    } catch (err) {
      console.error('Group production failed', err);
      toast.error(
        err?.response?.data?.detail
        || err?.response?.data?.error
        || 'Failed to record production — check the work orders before retrying',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="group-production-dialog">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Factory /> Record Production — {groupLabel || 'combined work orders'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the total made — it fills the most urgent work order first —
            or edit each work order&apos;s share below to choose exactly where
            the units go. Every unit is signed to a specific customer order.
          </Typography>

          {capacityLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Checking what each work order can make…
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              label={`Quantity made (group needs ${groupRemaining})`}
              type="number"
              size="medium"
              fullWidth
              autoFocus
              value={qty}
              onChange={(e) => handleTotalChange(e.target.value)}
              inputProps={{ 'data-testid': 'group-qty-input', min: 0 }}
              sx={{ flex: 2 }}
            />
            <TextField
              label="Scrapped"
              type="number"
              size="medium"
              value={scrap}
              onChange={(e) => setScrap(e.target.value)}
              error={scrapNegative}
              helperText={scrapNegative ? 'cannot be negative' : 'total for the group'}
              inputProps={{ 'data-testid': 'group-scrap-input', min: 0 }}
              sx={{ flex: 1, minWidth: 120 }}
            />
          </Box>

          {leftover > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="group-leftover-warning">
              That&apos;s {leftover} more than these work orders need. Lower the
              quantity — overproduction must be recorded on an individual work
              order, not a group.
            </Alert>
          )}

          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>
            Where the units go
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Scrap is on top of the quantity made and doesn&apos;t count towards
            what a work order still needs — replacements still get made against
            the most urgent order.
          </Typography>
          <Stack spacing={1}>
            {orderedSources.map((s) => {
              const remaining = remainingOf(s);
              const eligible = isEligible(s);
              const rowVal = parseFloat(rowQty[s.step_id]) || 0;
              const rowOver = eligible && rowVal > remaining;
              const rowCap = capacity ? capacity[s.step_id] : null;
              const rowShort = eligible && rowCap != null && rowVal > rowCap;
              const highlighted = eligible && rowVal > 0;
              return (
                <Box
                  key={s.step_id}
                  data-testid={`group-split-${s.step_id}`}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                    p: 1, borderRadius: 1, border: '1px solid',
                    borderColor: rowOver ? 'error.main'
                      : highlighted ? 'primary.light' : 'divider',
                    bgcolor: highlighted ? 'primary.50' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight="bold">{s.wo_number}</Typography>
                      {s.customer_order_number && (
                        <Chip
                          label={s.customer_order_number}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.85rem', fontWeight: 700 }}
                        />
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        {s.customer_name || ''}
                      </Typography>
                      {s.customer_order_status && (
                        <Chip
                          label={orderStatusLabel(s.customer_order_status)}
                          size="small"
                          color={orderStatusColor(s.customer_order_status)}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {eligible && rowCap != null && (
                        <Box
                          component="span"
                          data-testid={`group-row-canmake-${s.step_id}`}
                          sx={{ fontWeight: 700, color: rowCap >= remaining ? 'success.main' : 'warning.main' }}
                        >
                          can make {rowCap} ·{' '}
                        </Box>
                      )}
                      needs {remaining}
                      {s.due_date ? ` · due ${new Date(s.due_date).toLocaleDateString()}` : ''}
                    </Typography>
                    {/* Sub-assemblies that have to be made before this one. */}
                    {Array.isArray(s.prerequisites) && s.prerequisites.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {s.prerequisites.map((p) => (
                          <Box
                            key={p.work_order_id || p.wo_number}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
                          >
                            <SubdirectoryArrowRight sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              needs first: <strong>{p.wo_number}</strong>{' '}
                              {p.output_item_name} ({p.output_item_code}) ×{' '}
                              {parseFloat(p.quantity_to_produce) || 0}
                            </Typography>
                            <Chip
                              label={p.status}
                              size="small"
                              color={p.status === 'completed' ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {eligible ? (
                    <TextField
                      size="small"
                      type="number"
                      label="Make"
                      value={rowQty[s.step_id] ?? ''}
                      onChange={(e) => handleRowChange(s, e.target.value)}
                      error={rowOver || rowShort}
                      helperText={
                        rowOver ? `max ${remaining}`
                          : rowShort ? `only ${rowCap} makeable` : ' '
                      }
                      inputProps={{
                        min: 0,
                        'data-testid': `group-row-qty-${s.step_id}`,
                      }}
                      sx={{ width: 100 }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.disabled" fontWeight="bold">
                      —
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={submitting ? <CircularProgress size={16} /> : <PlayArrow />}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="group-production-submit"
          >
            {submitting ? 'Recording…' : 'Sign off all'}
          </Button>
        </DialogActions>
      </Dialog>

      {shortage && (
        <MaterialShortageDialogV2
          open
          onClose={() => setShortage(null)}
          woNumber={shortage.woNumber}
          shortages={shortage.shortages}
          intro="Nothing was recorded. This work order's materials are short:"
        />
      )}
    </>
  );
}
